const NO_ROLE = Object.freeze({
  role: 'none',
  category: null,
  types: [],
  partnerTypes: [],
  primaryType: null,
})

const displayRank = type => [
  type.is_interior ? 1 : 0,
  type.position ?? 0,
  type.id,
]

export function compareHeadlightTypes(a, b) {
  const left = displayRank(a)
  const right = displayRank(b)
  const index = left.findIndex((value, i) => value !== right[i])

  return index === -1 ? 0 : left[index] - right[index]
}

export function sharedBulbGroups(blocks, sharedBulbs) {
  const types = new Map(
    (blocks || []).map(block => [block.headlight_type.id, block.headlight_type])
  )

  return (Array.isArray(sharedBulbs) ? sharedBulbs : [])
    .filter(group => group && group.headlight_category)
    .map(group => ({
      category: {
        id: group.headlight_category.id,
        name: group.headlight_category.name,
      },
      types: [...new Set(group.headlight_type_ids || [])]
        .map(id => types.get(id))
        .filter(Boolean)
        .sort(compareHeadlightTypes)
        .map(type => ({ id: type.id, name: type.name })),
    }))
    .filter(group => group.types.length >= 2)
}

export function groupsOfType(groups, typeId) {
  return (groups || []).filter(group =>
    group.types.some(type => type.id === typeId)
  )
}

export function sharedBulbRoles(groups, typeId, detail) {
  if (!detail) return []

  const ofType = groupsOfType(groups, typeId)

  return (detail.headlight_categories || []).flatMap(category => {
    const group = ofType.find(
      candidate => candidate.category.id === category.id
    )
    if (!group) return []

    const [primaryType] = group.types

    return [
      {
        role: primaryType.id === typeId ? 'primary' : 'secondary',
        category: group.category,
        types: group.types,
        partnerTypes: group.types.filter(type => type.id !== typeId),
        primaryType,
      },
    ]
  })
}

export function sharedBulbRole(groups, typeId, detail) {
  const roles = sharedBulbRoles(groups, typeId, detail)

  return roles.find(role => role.role === 'primary') || roles[0] || NO_ROLE
}

export function isFullyShared(groups, typeId, detail) {
  const categories = detail?.headlight_categories || []
  if (!categories.length) return false

  const roles = sharedBulbRoles(groups, typeId, detail)

  return (
    roles.length === categories.length &&
    roles.every(
      role =>
        role.role === 'secondary' &&
        role.primaryType.id === roles[0].primaryType.id
    )
  )
}

export function normalizeDetailName(name) {
  return String(name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export function pairedDetail(sourceDetail, targetDetails, categoryId) {
  const candidates = (targetDetails || []).filter(detail =>
    (detail.headlight_categories || []).some(
      category => category.id === categoryId
    )
  )

  if (candidates.length === 1) return candidates[0]
  if (!sourceDetail || candidates.length < 2) return null

  const name = normalizeDetailName(sourceDetail.name)
  const matches = candidates.filter(
    detail => normalizeDetailName(detail.name) === name
  )

  return matches.length === 1 ? matches[0] : null
}

export function sharedDetailSelections(groups, blocks, typeId, detailId) {
  const blockOf = id =>
    (blocks || []).find(block => block.headlight_type.id === id)
  const detail = (blockOf(typeId)?.details || []).find(
    candidate => candidate.id === detailId
  )
  const selections = {}

  sharedBulbRoles(groups, typeId, detail).forEach(role => {
    role.partnerTypes.forEach(partner => {
      if (partner.id in selections) return

      const paired = pairedDetail(
        detail,
        blockOf(partner.id)?.details,
        role.category.id
      )
      if (paired) selections[partner.id] = paired.id
    })
  })

  return selections
}

export function joinTypeNames(types) {
  return (types || []).map(type => type.name).join(' + ')
}

export function detailCarriesCategory(detail, categoryId) {
  return (detail?.headlight_categories || []).some(
    category => category.id === categoryId
  )
}

export function defaultDetailId(block) {
  return (block?.details || []).length >= 2
    ? null
    : block?.default_detail_id ?? null
}

export function currentDetailId(block, selected) {
  const typeId = block.headlight_type.id

  return selected && typeId in selected
    ? selected[typeId]
    : defaultDetailId(block)
}

export function currentDetail(block, selected) {
  const detailId = currentDetailId(block, selected)
  if (detailId === null || detailId === undefined) return null

  const details = block.details || []

  return details.find(detail => detail.id === detailId) || details[0] || null
}

export function currentDetails(blocks, selected) {
  return (blocks || []).reduce((details, block) => {
    details[block.headlight_type.id] = currentDetail(block, selected)
    return details
  }, {})
}

export function isSharedRoleInEffect(role, details) {
  if (!role || role.role === 'none') return false

  if (role.role === 'secondary') {
    return detailCarriesCategory(
      details?.[role.primaryType.id],
      role.category.id
    )
  }

  return role.partnerTypes.every(partner => {
    const partnerDetail = details?.[partner.id]
    return (
      !partnerDetail || detailCarriesCategory(partnerDetail, role.category.id)
    )
  })
}

export function sharedRolesInEffect(groups, typeId, detail, details) {
  return sharedBulbRoles(groups, typeId, detail).filter(role =>
    isSharedRoleInEffect(role, details)
  )
}

export function includedSharedRole(groups, typeId, detail, details) {
  if (!isFullyShared(groups, typeId, detail)) return null

  const roles = sharedBulbRoles(groups, typeId, detail)

  return roles.every(role => isSharedRoleInEffect(role, details))
    ? roles[0]
    : null
}

export function kitSharedRole(groups, typeId, detail, details) {
  const role = sharedBulbRole(groups, typeId, detail)

  return role.role === 'primary' && isSharedRoleInEffect(role, details)
    ? role
    : null
}

export function emptyDetailSelection() {
  return { selected: {}, syncedFrom: {} }
}

function detailOfType(blocks, typeId, detailId) {
  const block = (blocks || []).find(
    candidate => candidate.headlight_type.id === typeId
  )

  return (block?.details || []).find(detail => detail.id === detailId) || null
}

function withPairedDetails(groups, blocks, selection, typeId, detailId) {
  const pairs = sharedDetailSelections(groups, blocks, typeId, detailId)
  const selected = { ...selection.selected }
  const syncedFrom = { ...selection.syncedFrom }

  Object.keys(pairs).forEach(partnerId => {
    selected[partnerId] = pairs[partnerId]
    syncedFrom[partnerId] = typeId
  })

  return { selected, syncedFrom }
}

export function selectionAfterChoice(
  groups,
  blocks,
  selection,
  typeId,
  detailId
) {
  const selected = { ...selection?.selected, [typeId]: detailId }
  const syncedFrom = { ...selection?.syncedFrom }
  delete syncedFrom[typeId]

  const detail = detailOfType(blocks, typeId, detailId)

  groupsOfType(groups, typeId)
    .filter(group => !detailCarriesCategory(detail, group.category.id))
    .forEach(group => {
      group.types.forEach(partner => {
        if (syncedFrom[partner.id] !== typeId) return
        delete selected[partner.id]
        delete syncedFrom[partner.id]
      })
    })

  return withPairedDetails(
    groups,
    blocks,
    { selected, syncedFrom },
    typeId,
    detailId
  )
}

function secondaryRoles(groups, blocks, typeId, detailId) {
  return sharedBulbRoles(
    groups,
    typeId,
    detailOfType(blocks, typeId, detailId)
  ).filter(role => role.role === 'secondary')
}

export function selectionForSharedKit(
  groups,
  blocks,
  selection,
  typeId,
  detailId
) {
  const current = selection || emptyDetailSelection()
  const roles = secondaryRoles(groups, blocks, typeId, detailId)
  if (!roles.length) return current

  const details = currentDetails(blocks, current.selected)
  const primaryCarries = roles.every(role =>
    detailCarriesCategory(details[role.primaryType.id], role.category.id)
  )

  return primaryCarries
    ? current
    : withPairedDetails(groups, blocks, current, typeId, detailId)
}

export function sharedKitTarget(groups, blocks, selection, typeId, detailId) {
  const [role] = secondaryRoles(groups, blocks, typeId, detailId)
  if (!role) return null

  const next = selectionForSharedKit(
    groups,
    blocks,
    selection,
    typeId,
    detailId
  )
  const primaryBlock = (blocks || []).find(
    block => block.headlight_type.id === role.primaryType.id
  )

  return {
    typeId: role.primaryType.id,
    selection: next,
    detail: primaryBlock ? currentDetail(primaryBlock, next.selected) : null,
  }
}

export function sharedKitComparison(target) {
  const count = target?.detail?.products_count || 0
  if (count < 2) return null

  const categories = target.detail.headlight_categories || []

  return {
    count,
    socket: categories.length === 1 ? categories[0].name : null,
  }
}

export const KIT_REVEAL_RATIO = 0.6

export function isKitRevealed(entry, ratio = KIT_REVEAL_RATIO) {
  if (!entry || !entry.isIntersecting) return false
  if (entry.intersectionRatio >= ratio) return true

  const rootHeight = entry.rootBounds?.height || 0

  return (
    rootHeight > 0 &&
    (entry.intersectionRect?.height || 0) >= rootHeight * ratio
  )
}

export const SCROLL_SETTLE_CHECKS = 2
export const SCROLL_IDLE_CHECKS = 5

export function isScrollSettled(moved, stillChecks) {
  return stillChecks >= (moved ? SCROLL_SETTLE_CHECKS : SCROLL_IDLE_CHECKS)
}
